import { cn } from '@plato/ui/util/classes';
import type { ReactNode } from 'react';

// Cross-fades between two variants sharing one slot. Both stay in flow; the inactive
// one collapses to zero height via a `grid-template-rows` 1fr -> 0fr transition, and
// the inner `overflow-hidden` clips its content as it shrinks, so the slot height
// animates smoothly between variants instead of snapping.
//
// `subgrid` threads the parent form's label/value columns through both layers (via
// `grid-cols-subgrid`) so their columns line up with the rest of the form. Collapsing
// height is what lets us keep the rows in flow instead of using `absolute`, which
// would break subgrid and snap the value sideways (and avoids `!important` fighting
// DialogForm.Item's baked-in subgrid). Without `subgrid` the layers are plain
// full-width blocks.
export type SwapSlotProps = {
    active: boolean;
    subgrid?: boolean;
    transition?: string;
    children: ReactNode;
};

export function SwapSlot({
    active,
    subgrid = false,
    transition = 'duration-300 ease-in-out',
    children,
}: SwapSlotProps): ReactNode {
    return (
        <div
            inert={!active}
            className={cn(
                'grid transition-[grid-template-rows,opacity]',
                transition,
                subgrid && 'col-span-2 grid-cols-subgrid',
                active ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0 pointer-events-none',
            )}
        >
            <div className={cn('overflow-hidden', subgrid && 'col-span-2 grid grid-cols-subgrid gap-3')}>
                {children}
            </div>
        </div>
    );
}
